import React, { useState, useEffect } from 'react';
import { Megaphone, Plus, Pencil, Trash2, ExternalLink, Send, X, Eye, EyeOff, Link2, Image as ImageIcon, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import axios from 'axios';
import { API } from '@/utils/api';
import { toast } from 'sonner';

const COMM_TYPES = {
  info: { label: 'Information', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  nouveaute: { label: 'Nouveaute', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  promotion: { label: 'Promotion', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  mise_a_jour: { label: 'Mise a jour', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
};

const emptyForm = { title: '', content: '', comm_type: 'info', link_url: '', link_label: '', image_url: '', expires_at: '', target: 'all' };

const SuperAdminCommunications = () => {
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/saas/communications`);
      setComms(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setShowDialog(true); };
  const openEdit = (c) => {
    setForm({ title: c.title, content: c.content, comm_type: c.comm_type, link_url: c.link_url || '', link_label: c.link_label || '', image_url: c.image_url || '', expires_at: c.expires_at ? c.expires_at.slice(0, 10) : '', target: c.target || 'all' });
    setEditId(c.id);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Titre et contenu requis'); return; }
    setSaving(true);
    try {
      const payload = { ...form, expires_at: form.expires_at || null };
      if (editId) {
        await axios.put(`${API}/saas/communications/${editId}`, payload);
        toast.success('Communication mise a jour');
      } else {
        await axios.post(`${API}/saas/communications`, payload);
        toast.success('Communication envoyee a tous les garages');
      }
      setShowDialog(false);
      load();
    } catch (e) { toast.error('Erreur'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette communication ?')) return;
    try {
      await axios.delete(`${API}/saas/communications/${id}`);
      toast.success('Communication supprimee');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/saas/communications/${c.id}`, { is_active: !c.is_active });
      toast.success(c.is_active ? 'Communication desactivee' : 'Communication activee');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="saas-communications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-blue-600" />Communications
            </h1>
            <p className="text-sm text-slate-500 mt-1">Envoyer des annonces, promotions et mises a jour a tous les garages</p>
          </div>
          <Button onClick={openNew} className="gap-2 rounded-xl" data-testid="new-communication-btn">
            <Plus className="w-4 h-4" />Nouvelle communication
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Chargement...</div>
        ) : comms.length === 0 ? (
          <Card className="text-center py-16 rounded-2xl border-dashed border-2">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Aucune communication</p>
            <p className="text-sm text-slate-400 mt-1">Creez votre premiere annonce pour tous les garages</p>
            <Button onClick={openNew} variant="outline" className="mt-4 gap-2 rounded-xl">
              <Plus className="w-4 h-4" />Creer
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {comms.map(c => {
              const typeInfo = COMM_TYPES[c.comm_type] || COMM_TYPES.info;
              const isExpired = c.expires_at && new Date(c.expires_at) < new Date();
              return (
                <Card key={c.id} className={`rounded-2xl p-5 transition-all ${!c.is_active || isExpired ? 'opacity-50' : ''}`} data-testid={`comm-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`text-xs ${typeInfo.color} border-0`}>{typeInfo.label}</Badge>
                        {!c.is_active && <Badge variant="outline" className="text-xs">Desactivee</Badge>}
                        {isExpired && <Badge variant="destructive" className="text-xs">Expiree</Badge>}
                        {c.target !== 'all' && <Badge variant="outline" className="text-xs">Cible</Badge>}
                      </div>
                      <h3 className="font-bold text-slate-900 dark:text-white text-lg">{c.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 whitespace-pre-wrap">{c.content}</p>
                      {c.link_url && (
                        <a href={c.link_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium">
                          <ExternalLink className="w-3.5 h-3.5" />{c.link_label || c.link_url}
                        </a>
                      )}
                      {c.image_url && (
                        <div className="mt-3">
                          <img src={c.image_url} alt="" className="h-20 rounded-lg object-cover" />
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                        <span>Cree le {new Date(c.created_at).toLocaleDateString('fr-CH')}</span>
                        {c.expires_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Expire le {new Date(c.expires_at).toLocaleDateString('fr-CH')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => toggleActive(c)} title={c.is_active ? 'Desactiver' : 'Activer'} data-testid={`toggle-comm-${c.id}`}>
                        {c.is_active ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-comm-${c.id}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} data-testid={`delete-comm-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="comm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                {editId ? 'Modifier la communication' : 'Nouvelle communication'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Type</label>
                <Select value={form.comm_type} onValueChange={v => setForm(f => ({ ...f, comm_type: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="comm-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(COMM_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Titre</label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Ex: Nouvelle fonctionnalite disponible !" className="rounded-xl" data-testid="comm-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Contenu</label>
                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Decrivez votre annonce, promotion, mise a jour..."
                  className="w-full min-h-[100px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm resize-y"
                  data-testid="comm-content-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1"><Link2 className="w-3 h-3" />Lien (URL)</label>
                  <Input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
                    placeholder="https://..." className="rounded-xl" data-testid="comm-link-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Texte du lien</label>
                  <Input value={form.link_label} onChange={e => setForm(f => ({ ...f, link_label: e.target.value }))}
                    placeholder="Decouvrir" className="rounded-xl" data-testid="comm-link-label-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" />Image (URL)</label>
                  <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    placeholder="https://..." className="rounded-xl" data-testid="comm-image-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" />Expiration</label>
                  <Input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                    className="rounded-xl" data-testid="comm-expires-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl">Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="rounded-xl gap-2" data-testid="comm-save-btn">
                <Send className="w-4 h-4" />{editId ? 'Mettre a jour' : 'Publier'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminCommunications;
